import { zodResolver } from '@hookform/resolvers/zod';
import { Vendor } from '@kalos/kalos-rpc';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kalos/ui';
import React, { type FC, useCallback, useEffect, useMemo } from 'react';
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { USA_STATES_OPTIONS } from '../../../../constants';
import { NET_TERMS_SELECT_OPTIONS } from './constants/paymentTerms';

const MIN_SIMILAR_MATCH_LENGTH = 2;
const MAX_SIMILAR_VENDORS_SHOWN = 5;

const normalizeVendorName = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ');

const findSimilarVendors = (
  existingVendors: Vendor[],
  typedName: string,
  editingVendor?: Vendor | null,
) => {
  const typed = normalizeVendorName(typedName);
  if (typed.length < MIN_SIMILAR_MATCH_LENGTH) return [];
  return existingVendors.filter((candidate) => {
    if (editingVendor && candidate.id === editingVendor.id) return false;
    const existing = normalizeVendorName(candidate.vendorName);
    if (existing.length < MIN_SIMILAR_MATCH_LENGTH) return false;
    return existing.includes(typed) || typed.includes(existing);
  });
};

const formatVendorAddress = (vendor: Vendor) =>
  [vendor.vendorAddress, vendor.vendorCity, vendor.vendorState, vendor.vendorZip]
    .filter(Boolean)
    .join(', ');

const createVendorFormSchema = (existingVendors: Vendor[], editingVendor?: Vendor | null) =>
  z.object({
    vendorName: z
      .string()
      .min(1, 'Vendor name is required')
      .refine((name) => {
        // Allow the same name if we're editing the same vendor
        if (editingVendor && editingVendor.vendorName.toLowerCase() === name.toLowerCase()) {
          return true;
        }
        // Check if name already exists (case insensitive)
        return !existingVendors.some(
          (vendor) => vendor.vendorName.toLowerCase() === name.toLowerCase(),
        );
      }, 'A vendor with this name already exists'),
    vendorAddress: z.string().optional(),
    vendorCity: z.string().optional(),
    vendorState: z
      .string()
      .refine(
        (value) => {
          // Allow empty value (optional field)
          if (!value || value.length === 0) return true;
          // Validate that the state is in the USA_STATES_OPTIONS list
          return USA_STATES_OPTIONS.includes(value);
        },
        { message: 'Please select a valid US state' },
      )
      .optional(),
    vendorZip: z
      .string()
      .refine(
        (value) => {
          // Allow empty value (optional field)
          if (!value || value.length === 0) return true;
          // Validate zip code format: 5 digits or 5+4 format
          return /^\d{5}(-\d{4})?$/.test(value);
        },
        { message: 'ZIP code must be 5 digits or 5+4 format (e.g., 12345 or 12345-6789)' },
      )
      .optional(),
    netTerms: z.string().default('0'),
  });

interface VendorCrudFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (vendor: Vendor) => Promise<void>;
  onSelectExisting: (vendor: Vendor) => void;
  vendor?: Vendor | null;
  isLoading?: boolean;
  existingVendors: Vendor[];
}

type VendorFormData = {
  vendorName: string;
  vendorAddress?: string;
  vendorCity?: string;
  vendorState?: string;
  vendorZip?: string;
  netTerms: string;
};

const getDefaultValues = (vendor?: Vendor | null): VendorFormData => ({
  vendorName: vendor?.vendorName || '',
  vendorAddress: vendor?.vendorAddress || '',
  vendorCity: vendor?.vendorCity || '',
  vendorState: vendor?.vendorState || '',
  vendorZip: vendor?.vendorZip || '',
  netTerms: vendor?.netTerms ? vendor.netTerms.toString() : '0',
});

export const VendorCrudForm: FC<VendorCrudFormProps> = ({
  open,
  onClose,
  onSave,
  onSelectExisting,
  vendor,
  isLoading = false,
  existingVendors,
}) => {
  const isEditing = !!vendor;

  const vendorFormSchema = useMemo(
    () => createVendorFormSchema(existingVendors, vendor),
    [existingVendors, vendor],
  );

  const form = useForm<VendorFormData>({
    resolver: zodResolver(vendorFormSchema),
    defaultValues: getDefaultValues(vendor),
  });

  const typedVendorName = useWatch({ control: form.control, name: 'vendorName' });

  const similarVendors = useMemo(
    () => findSimilarVendors(existingVendors, typedVendorName ?? '', vendor),
    [existingVendors, typedVendorName, vendor],
  );
  const shownSimilarVendors = similarVendors.slice(0, MAX_SIMILAR_VENDORS_SHOWN);
  const hiddenSimilarCount = similarVendors.length - shownSimilarVendors.length;

  // Reset form with correct data when vendor changes or modal opens
  useEffect(() => {
    if (open) {
      form.reset(getDefaultValues(vendor));
    }
  }, [open, vendor, form]);

  const handleSubmit: SubmitHandler<VendorFormData> = useCallback(
    async (data) => {
      const netTermsValue = parseInt(data.netTerms, 10);
      const vendorData = Vendor.create({
        ...vendor,
        ...data,
        netTerms: isNaN(netTermsValue) || netTermsValue === 0 ? 0 : netTermsValue,
        isActive: 1,
      });

      // Add fieldMask for updates
      if (vendor?.id) {
        vendorData.fieldMask = [
          'VendorName',
          'VendorAddress',
          'VendorCity',
          'VendorState',
          'VendorZip',
          'NetTerms',
        ];
      }

      // Only save the vendor - don't do anything else
      await onSave(vendorData);
      form.reset();

      // Parent (VendorSelectionModal) will handle closing this form
      // and refreshing the vendor list
    },
    [vendor, onSave, form],
  );

  const handleClose = useCallback(() => {
    form.reset(getDefaultValues(vendor));
    onClose();
  }, [form, vendor, onClose]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Vendor' : 'Add New Vendor'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit(handleSubmit)(e);
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="vendorName"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Vendor Name *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter vendor name" />
                    </FormControl>
                    <FormMessage />
                    {shownSimilarVendors.length > 0 && (
                      <div className="space-y-2 rounded-md border p-3 text-sm">
                        <p className="text-muted-foreground">Possible existing vendors</p>
                        {shownSimilarVendors.map((similarVendor) => {
                          const address = formatVendorAddress(similarVendor);
                          return (
                            <div
                              key={similarVendor.id}
                              className="flex items-center justify-between gap-2"
                            >
                              <div className="min-w-0">
                                <p className="truncate font-medium">{similarVendor.vendorName}</p>
                                {address && (
                                  <p className="text-muted-foreground truncate text-xs">
                                    {address}
                                  </p>
                                )}
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => onSelectExisting(similarVendor)}
                              >
                                Use this vendor
                              </Button>
                            </div>
                          );
                        })}
                        {hiddenSimilarCount > 0 && (
                          <p className="text-muted-foreground text-xs">
                            +{hiddenSimilarCount} more matches
                          </p>
                        )}
                      </div>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="vendorAddress"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Street address" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="vendorCity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="City" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="vendorState"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {USA_STATES_OPTIONS.map((state) => {
                          const stateValue = typeof state === 'string' ? state : state.value;
                          const stateLabel = typeof state === 'string' ? state : state.label;
                          return (
                            <SelectItem key={String(stateValue)} value={String(stateValue)}>
                              {String(stateLabel)}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="vendorZip"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ZIP Code</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="ZIP code" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="netTerms"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Payment Terms</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select payment terms" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {NET_TERMS_SELECT_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading} isLoading={isLoading}>
                {isEditing ? 'Update Vendor' : 'Create Vendor'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
